import { useState, useEffect } from 'react';
import { useAsyncData, useAsyncMutation } from './useAsyncData';
import type {
  BackofficeMetrics,
  BackofficeRecentActivity,
  BackofficeUser,
  BackofficeCompany,
  BackofficeCompanyDetail,
  BackofficeStory,
  StoryResource,
  FullStory,
  UserFilters,
  CompanyFilters,
  StoryFilters,
  UserFormData,
  CompanyFormData,
  StoryFormData,
  BackofficeChartDataPoint,
} from '@/types/backoffice.types';
import type { Activity } from '@/types/story.types';
import * as backofficeApi from '@/services/api/backoffice.api';

export function useBackofficeMetrics() {
  const { data: metrics, isLoading, error } = useAsyncData<BackofficeMetrics>({
    fetchFn: backofficeApi.getBackofficeMetrics,
  });
  return { metrics, isLoading, error };
}

export function useBackofficeRecentActivity() {
  const { data, isLoading, error } = useAsyncData<BackofficeRecentActivity[]>({
    fetchFn: backofficeApi.getBackofficeRecentActivity,
  });
  return { activities: data ?? [], isLoading, error };
}

export function useUsers(filters?: UserFilters) {
  const { data, isLoading, error } = useAsyncData<{ users: BackofficeUser[]; total: number }>({
    fetchFn: () => backofficeApi.getUsers(filters),
    dependencies: [filters],
  });
  return { users: data?.users ?? [], total: data?.total ?? 0, isLoading, error };
}

export function useUser(userId: string | undefined) {
  const { data: user, isLoading, error } = useAsyncData<BackofficeUser | null>({
    fetchFn: () => backofficeApi.getUserById(userId!),
    dependencies: [userId],
    enabled: !!userId,
  });
  return { user, isLoading, error };
}

export function useSaveUser() {
  const { mutate, isLoading: isSaving, error } = useAsyncMutation<
    BackofficeUser,
    { userId: string | null; data: UserFormData }
  >({
    mutationFn: ({ userId, data }) => backofficeApi.saveUser(userId, data),
  });
  return {
    saveUser: (userId: string | null, data: UserFormData) => mutate({ userId, data }),
    isSaving,
    error,
  };
}

export function useDeleteUser() {
  const { mutate: deleteUser, isLoading: isDeleting, error } = useAsyncMutation<void, string>({
    mutationFn: backofficeApi.deleteUser,
  });
  return { deleteUser, isDeleting, error };
}

export function useSetUserActive() {
  const { mutate, isLoading: isSettingActive, error } = useAsyncMutation<
    BackofficeUser,
    { userId: string; isActive: boolean }
  >({
    mutationFn: ({ userId, isActive }) => backofficeApi.setUserActive(userId, isActive),
  });
  return {
    setUserActive: (userId: string, isActive: boolean) => mutate({ userId, isActive }),
    isSettingActive,
    error,
  };
}

export function useSetUserCompanyAdmin() {
  const { mutate, isLoading: isSettingCompanyAdmin, error } = useAsyncMutation<
    BackofficeUser,
    { userId: string; isCompanyAdmin: boolean }
  >({
    mutationFn: ({ userId, isCompanyAdmin }) =>
      backofficeApi.setUserCompanyAdmin(userId, isCompanyAdmin),
  });
  return {
    setUserCompanyAdmin: (userId: string, isCompanyAdmin: boolean) =>
      mutate({ userId, isCompanyAdmin }),
    isSettingCompanyAdmin,
    error,
  };
}

export function useCompanies(filters?: CompanyFilters) {
  const { data, isLoading, error } = useAsyncData<{
    companies: BackofficeCompany[];
    total: number;
  }>({
    fetchFn: () => backofficeApi.getCompanies(filters),
    dependencies: [filters],
  });
  return { companies: data?.companies ?? [], total: data?.total ?? 0, isLoading, error };
}

export function useCompany(companyId: string | undefined) {
  const { data: company, isLoading, error } = useAsyncData<BackofficeCompanyDetail | null>({
    fetchFn: () => backofficeApi.getCompanyById(companyId!),
    dependencies: [companyId],
    enabled: !!companyId,
  });
  return { company, isLoading, error };
}

export function useSaveCompany() {
  const { mutate, isLoading: isSaving, error } = useAsyncMutation<
    BackofficeCompanyDetail,
    { companyId: string | null; data: CompanyFormData }
  >({
    mutationFn: ({ companyId, data }) => backofficeApi.saveCompany(companyId, data),
  });
  return {
    saveCompany: (companyId: string | null, data: CompanyFormData) => mutate({ companyId, data }),
    isSaving,
    error,
  };
}

export function useDeleteCompany() {
  const { mutate: deleteCompany, isLoading: isDeleting, error } = useAsyncMutation<void, string>({
    mutationFn: backofficeApi.deleteCompany,
  });
  return { deleteCompany, isDeleting, error };
}

export function useStories(filters?: StoryFilters) {
  const { data, isLoading, error, refetch } = useAsyncData<{
    stories: BackofficeStory[];
    total: number;
  }>({
    fetchFn: () => backofficeApi.getStories(filters),
    dependencies: [filters],
  });
  return { stories: data?.stories ?? [], total: data?.total ?? 0, isLoading, error, refetch };
}

export function useStory(storyId: string | undefined) {
  const { data: story, isLoading, error } = useAsyncData<FullStory | null>({
    fetchFn: () => backofficeApi.getStoryById(storyId!),
    dependencies: [storyId],
    enabled: !!storyId,
  });
  return { story, isLoading, error };
}

export function useSaveStory() {
  const { mutate, isLoading: isSaving, error } = useAsyncMutation<
    BackofficeStory,
    { storyId: string | null; data: StoryFormData }
  >({
    mutationFn: ({ storyId, data }) => backofficeApi.saveStory(storyId, data),
  });
  return {
    saveStory: (storyId: string | null, data: StoryFormData) =>
      mutate({ storyId, data }),
    isSaving,
    error,
  };
}

export function useDeleteStory() {
  const { mutate: deleteStory, isLoading: isDeleting, error } = useAsyncMutation<void, string>({
    mutationFn: backofficeApi.deleteStory,
  });
  return { deleteStory, isDeleting, error };
}

export function useChartData(
  metric: 'users' | 'completions' | 'revenue',
  period: '7d' | '30d' | '90d' | '1y'
) {
  const { data, isLoading, error } = useAsyncData<BackofficeChartDataPoint[]>({
    fetchFn: () => backofficeApi.getChartData(metric, period),
    dependencies: [metric, period],
  });
  return { data: data ?? [], isLoading, error };
}

export function useResources(storyId?: string) {
  const [resources, setResources] = useState<StoryResource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchResources = async () => {
    if (!storyId) return;
    try {
      setIsLoading(true);
      const data = await backofficeApi.getResources(storyId);
      setResources(data.sort((a, b) => a.order - b.order));
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!storyId) {
      setIsLoading(false);
      return;
    }
    void fetchResources();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const saveResource = async (resourceData: Partial<StoryResource>) => {
    if (!storyId) throw new Error('Story ID is required');
    try {
      const result = await backofficeApi.saveResource(storyId, resourceData);
      if (resourceData.id) {
        setResources((prev) => prev.map((r) => (r.id === result.id ? result : r)));
      } else {
        setResources((prev) => [...prev, result].sort((a, b) => a.order - b.order));
      }
      return result;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const deleteResource = async (resourceId: string) => {
    if (!storyId) throw new Error('Story ID is required');
    try {
      await backofficeApi.deleteResource(storyId, resourceId);
      setResources((prev) => prev.filter((r) => r.id !== resourceId));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return { resources, isLoading, error, saveResource, deleteResource, refetchResources: fetchResources };
}

export function useActivities(storyId?: string) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!storyId) {
      setIsLoading(false);
      return;
    }

    const fetchActivities = async () => {
      try {
        setIsLoading(true);
        const data = await backofficeApi.getActivities(storyId);
        setActivities(data.sort((a, b) => a.order - b.order));
        setError(null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchActivities();
  }, [storyId]);

  const saveActivity = async (activityData: Partial<Activity>) => {
    if (!storyId) throw new Error('Story ID is required');
    try {
      const result = await backofficeApi.saveActivity(storyId, activityData);
      if (activityData.id) {
        setActivities((prev) =>
          prev.map((activity) => (activity.id === result.id ? result : activity))
        );
      } else {
        setActivities((prev) => [...prev, result].sort((a, b) => a.order - b.order));
      }
      return result;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const deleteActivity = async (activityId: string) => {
    if (!storyId) throw new Error('Story ID is required');
    try {
      await backofficeApi.deleteActivity(storyId, activityId);
      setActivities((prev) => prev.filter((activity) => activity.id !== activityId));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const reorderActivity = async (activityId: string, newOrder: number) => {
    if (!storyId) throw new Error('Story ID is required');
    try {
      const result = await backofficeApi.reorderActivity(storyId, activityId, newOrder);
      setActivities((prev) => {
        const updated = prev.map((activity) =>
          activity.id === activityId ? { ...activity, order: newOrder } : activity
        );
        return updated.sort((a, b) => a.order - b.order);
      });
      return result;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return { activities, isLoading, error, saveActivity, deleteActivity, reorderActivity };
}
